export default `
# Webhook (Watcher)

A webhook is an HTTP callback that lets PostaMail notify your application automatically when a relevant event occurs, instead of requiring it to repeatedly poll the API. Your application exposes a URL and PostaMail sends a request to it when new unread returns are available. This event-driven approach reduces unnecessary requests and lets your system start processing new returns sooner. Notifications are batched; they are not sent in real time for each individual return.

The PostaMail webhook that monitors returns for shipments in progress is called a **Watcher**. A Watcher notifies your system when new unread returns are available, so it can retrieve and process them through the Inbox API.

Use the PRE environment for every test: \`https://pre-aws.postamail.es/api\`.

## Register a webhook watcher {#register-a-webhook-watcher}

\`\`\`endpoint
PUT /api/inbox/watchers
\`\`\`

| Field | Value |
|-------|-------|
| URL | \`https://pre-aws.postamail.es/api/inbox/watchers\` |
| Operation | \`PUT\` |
| Header | \`Authorization: Bearer <jwtToken>\`; \`Content-Type: application/json\` |
| Body | Watcher registration object |

\`\`\`json
{
  "callbackUrl": "https://<dominio-postamail>/postamail/returns",
  "callbackHeader": "<optional bearer token for your callback endpoint>",
  "authorizationType": "BEARER_TOKEN",
  "apiKeyHeaderElement": null,
  "apiKeyValue": null,
  "callbackResponse": "POSTAMAIL_OK",
  "submitMode": "pull"
}
\`\`\`

\`\`\`tabs
===curl
curl -X PUT "https://pre-aws.postamail.es/api/inbox/watchers" \\
  -H "Authorization: Bearer <jwtToken>" \\
  -H "Content-Type: application/json" \\
  -d @watcher.json
===node
const response = await fetch('https://pre-aws.postamail.es/api/inbox/watchers', {
  method: 'PUT',
  headers: { Authorization: 'Bearer ' + jwtToken, 'Content-Type': 'application/json' },
  body: JSON.stringify(watcher)
})
const unreadAtRegistration = await response.json()
===python
response = requests.put(
  'https://pre-aws.postamail.es/api/inbox/watchers',
  headers={'Authorization': 'Bearer ' + jwt_token},
  json=watcher
)
unread_at_registration = response.json()
===php
<?php
$jwtToken = '<jwtToken>';
$watcher = array(
  'callbackUrl' => 'https://<dominio-postamail>/postamail/returns',
  'callbackHeader' => '<optional bearer token for your callback endpoint>',
  'authorizationType' => 'BEARER_TOKEN',
  'apiKeyHeaderElement' => null,
  'apiKeyValue' => null,
  'callbackResponse' => 'POSTAMAIL_OK',
  'submitMode' => 'pull'
);
$curl = curl_init('https://pre-aws.postamail.es/api/inbox/watchers');
curl_setopt_array($curl, array(
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_CUSTOMREQUEST => 'PUT',
  CURLOPT_HTTPHEADER => array('Authorization: Bearer ' . $jwtToken, 'Content-Type: application/json'),
  CURLOPT_POSTFIELDS => json_encode($watcher)
));
$unreadAtRegistration = json_decode(curl_exec($curl), true);
curl_close($curl);
===csharp
using var request = new HttpRequestMessage(HttpMethod.Put, "https://pre-aws.postamail.es/api/inbox/watchers")
{
  Content = JsonContent.Create(watcher)
};
request.Headers.Authorization = new AuthenticationHeaderValue("Bearer", jwtToken);
using var response = await client.SendAsync(request);
var unreadAtRegistration = await response.Content.ReadFromJsonAsync<int>();
===java
var request = HttpRequest.newBuilder()
  .uri(URI.create("https://pre-aws.postamail.es/api/inbox/watchers"))
  .header("Authorization", "Bearer " + jwtToken)
  .header("Content-Type", "application/json")
  .PUT(HttpRequest.BodyPublishers.ofString(watcherJson))
  .build();
var response = client.send(request, HttpResponse.BodyHandlers.ofString());
\`\`\`

## Watcher object

| Field | Type | Description |
|-------|------|-------------|
| \`callbackUrl\` | string | Absolute URL PostaMail will call when new returns are available. |
| \`callbackHeader\` | string | Optional bearer token value sent to your callback endpoint. |
| \`authorizationType\` | string | \`BEARER_TOKEN\` or \`API_KEY\`. |
| \`apiKeyHeaderElement\` | string | Custom API-key header name when using API-key callbacks. |
| \`apiKeyValue\` | string | Custom API-key value when using API-key callbacks. |
| \`callbackResponse\` | string | Text expected in your callback response body. |
| \`submitMode\` | string | \`pull\` for notification-only mode. \`push\` is reserved for direct return submission. |

## Callback request sent by PostaMail {#callback-request-sent-by-postamail}

PostaMail calls your \`callbackUrl\` with \`POST\` and counters in the query string:

\`\`\`endpoint
POST https://<dominio-postamail>/postamail/returns
\`\`\`

\`\`\`text
https://<dominio-postamail>/postamail/returns?total=10&states=5&proofs=2&certs=3&documents=0&stampedcopies=0&reports=0
\`\`\`

If \`callbackHeader\` is configured with bearer-token mode, PostaMail sends it as:

\`\`\`text
Authorization: Bearer <callbackHeader>
\`\`\`

An API-key callback mode is also available. In that case, PostaMail sends the configured custom header:

\`\`\`text
<apiKeyHeaderElement>: <apiKeyValue>
\`\`\`

Your callback endpoint must return HTTP \`200 OK\` or \`205 Reset Content\` and include \`callbackResponse\` in the response body. If the response is incorrect four times, the notification is considered failed; repeated failed notifications can stop future notifications.

| Callback response | Effect |
|-------------------|--------|
| \`200 OK\` with \`callbackResponse\` | Notification accepted. Returns remain unread; your system should call the polling endpoints and then mark them as read. |
| \`205 Reset Content\` with \`callbackResponse\` | Notification accepted and PostaMail marks all currently available unread returns as read automatically. |

The callback does not include return details. On receiving it, call \`GET /api/inbox/feedback\`, process the new returns, and mark them as read with \`PUT /api/inbox/feedback/read\`.
`



// WEBPACK FOOTER //
// ./src/components/docs/api/content/webhooks.js